import {
  AppearanceContext,
  appearanceSettingsKey,
  readAppearance,
  cacheAppearance,
  type AppearanceMode,
} from "./appearance";
import { useLayoutEffect, useMemo, type PropsWithChildren } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@features/auth/context/AuthContext";
import { authStorage } from "@features/auth/lib/auth-storage";
import {
  profileApi,
  type ProfileSettings,
} from "@features/profile/api/profile-api";
import { createAppTheme } from "./appTheme";

export function AppearanceProvider({ children }: PropsWithChildren) {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const client = useQueryClient();
  let cachedId: number | string | undefined;
  try {
    cachedId = isInitializing ? authStorage.get()?.user.id : undefined;
  } catch {
    /* No cached session. */
  }
  const userId = user?.id ?? cachedId;
  const key = appearanceSettingsKey(userId);
  const settings = useQuery({
    queryKey: key,
    queryFn: profileApi.settings,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const mode: AppearanceMode =
    isAuthenticated && settings.data
      ? settings.data.darkMode
        ? "dark"
        : "light"
      : readAppearance(userId);
  const mutation = useMutation({
    mutationFn: (next: AppearanceMode) =>
      profileApi.updateSettings({ darkMode: next === "dark" }),
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<ProfileSettings>(key);
      client.setQueryData<ProfileSettings>(key, (current) =>
        current ? { ...current, darkMode: next === "dark" } : current,
      );
      return { previous, key };
    },
    onError: (_error, _next, context) => {
      if (context?.previous)
        client.setQueryData<ProfileSettings>(context.key, (current) =>
          current
            ? { ...current, darkMode: context.previous!.darkMode }
            : context.previous,
        );
    },
    onSuccess: (saved, _next, context) => {
      // Do not overwrite independently edited notification preferences.
      client.setQueryData<ProfileSettings>(context?.key ?? key, (current) =>
        current ? { ...current, darkMode: saved.darkMode } : saved,
      );
    },
  });
  useLayoutEffect(() => {
    document.documentElement.dataset.orbitTheme = mode;
    document.documentElement.style.colorScheme = mode;
    document.documentElement.style.backgroundColor =
      mode === "dark" ? "#08121c" : "#F5F7FB";
    if (userId != null) cacheAppearance(userId, mode);
  }, [mode, userId]);
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  return (
    <AppearanceContext.Provider
      value={{
        mode,
        setMode: (next) => {
          if (isAuthenticated && settings.data && !mutation.isPending)
            mutation.mutate(next);
        },
        saving: mutation.isPending,
        loading: !isAuthenticated || !settings.data,
        error: mutation.isError
          ? "Could not save appearance. Your previous theme has been restored."
          : settings.isError
            ? "Appearance settings could not be loaded. Try refreshing the page."
            : null,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppearanceContext.Provider>
  );
}
